import React, { useEffect, useState } from "react";
import { Paperclip, FileText, Loader2, Download, ImageOff } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import api from "@/lib/apiClient";

function isImage(path) {
  return /\.(jpg|jpeg|png|gif|webp)$/i.test(path);
}

function AttachmentThumb({ path, onOpen }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  const [ct, setCt] = useState("");
  const name = path.split("/").pop();

  useEffect(() => {
    let revoked = false;
    let objectUrl;
    api
      .get(`/files/${path}`, { responseType: "blob" })
      .then((res) => {
        if (revoked) return;
        setCt(res.data.type || "");
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => setErr(true));
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  const img = isImage(path) || ct.startsWith("image/");

  if (err) {
    return (
      <div className="h-24 w-24 rounded-lg border border-border bg-muted flex flex-col items-center justify-center text-muted-foreground" data-testid="attachment-error">
        <ImageOff className="h-5 w-5" />
        <span className="text-[9px] mt-1">Unavailable</span>
      </div>
    );
  }

  if (!url) {
    return (
      <div className="h-24 w-24 rounded-lg border border-border bg-muted flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (img) {
    return (
      <button
        type="button"
        onClick={() => onOpen({ url, img: true, name })}
        className="group relative h-24 w-24 overflow-hidden rounded-lg border border-border hover:ring-2 hover:ring-primary transition-all"
        data-testid="attachment-thumb"
      >
        <img src={url} alt={name} className="h-full w-full object-cover" />
        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen({ url, img: false, name })}
      className="h-24 w-24 rounded-lg border border-border bg-red-50 hover:ring-2 hover:ring-primary transition-all flex flex-col items-center justify-center text-red-600"
      data-testid="attachment-thumb"
    >
      <FileText className="h-6 w-6" />
      <span className="text-[9px] mt-1 px-1 truncate max-w-full">{name}</span>
    </button>
  );
}

export default function AttachmentGallery({ attachments = [] }) {
  const [active, setActive] = useState(null);

  if (!attachments.length) {
    return <p className="text-sm text-muted-foreground">No attachments provided.</p>;
  }

  return (
    <div data-testid="attachment-gallery">
      <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1 mb-2">
        <Paperclip className="h-3.5 w-3.5" /> Attachments ({attachments.length})
      </p>
      <div className="flex flex-wrap gap-3">
        {attachments.map((p, i) => (
          <AttachmentThumb key={i} path={p} onOpen={setActive} />
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl" data-testid="attachment-viewer">
          {active && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-medium truncate text-sm">{active.name}</p>
                <a href={active.url} download={active.name} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid="attachment-download">
                  <Download className="h-4 w-4" /> Download
                </a>
              </div>
              {active.img ? (
                <img src={active.url} alt={active.name} className="max-h-[70vh] w-full object-contain rounded-lg bg-muted" />
              ) : (
                <iframe src={active.url} title={active.name} className="w-full h-[70vh] rounded-lg border" />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
